import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import DailyIframe, { DailyCall } from '@daily-co/daily-js';
import { FiMapPin, FiVideo } from 'react-icons/fi';

const SalaCliente: React.FC = () => {
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const roomUrl = queryParams.get('roomUrl');
    
    const containerRef = useRef<HTMLDivElement>(null);
    const [geoPermissionStatus, setGeoPermissionStatus] = useState<'requesting' | 'granted' | 'denied'>('requesting');
    const [joined, setJoined] = useState(false);

    useEffect(() => {
        if (!roomUrl) return;

        let frame: DailyCall | null = null;
        let watchId: number | null = null;
        let heartbeatInterval: any = null;
        let currentPos: { lat: number; lon: number } | null = null;

        const sendGps = () => {
            if (frame && currentPos && frame.meetingState() === 'joined-meeting') {
                const payload = {
                    type: 'gps_update',
                    lat: currentPos.lat,
                    lon: currentPos.lon
                };
                frame.sendAppMessage(payload, '*');
                frame.sendAppMessage(JSON.stringify(payload), '*');
            }
        };

        const init = async () => {
            // Request geolocation
            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(
                    (position) => {
                        setGeoPermissionStatus('granted');
                        currentPos = {
                            lat: position.coords.latitude,
                            lon: position.coords.longitude
                        };
                        
                        // Initialize Daily
                        if (containerRef.current) {
                            frame = DailyIframe.createFrame(containerRef.current, {
                                iframeStyle: {
                                    width: '100%',
                                    height: '100%',
                                    border: 'none',
                                    borderRadius: '8px'
                                },
                                showLeaveButton: true
                            });
                            
                            frame.on('joined-meeting', () => {
                                setJoined(true);
                                sendGps();
                            });

                            frame.on('participant-joined', () => {
                                sendGps();
                            });

                            frame.on('app-message', (event: any) => {
                                let data = event?.data;
                                if (typeof data === 'string') {
                                    try { data = JSON.parse(data); } catch (e) {}
                                }
                                if (data && data.type === 'request_gps') {
                                    sendGps();
                                }
                            });
                            
                            frame.on('left-meeting', () => {
                                setJoined(false);
                            });

                            frame.join({ url: roomUrl });

                            // Heartbeat interval sending GPS every 4 seconds
                            heartbeatInterval = setInterval(() => {
                                sendGps();
                            }, 4000);

                            // Watch position to update live
                            watchId = navigator.geolocation.watchPosition((pos) => {
                                currentPos = {
                                    lat: pos.coords.latitude,
                                    lon: pos.coords.longitude
                                };
                                sendGps();
                            }, (err) => {
                                console.warn("Error tracking position:", err);
                            }, { enableHighAccuracy: true, maximumAge: 5000 });
                        }
                    },
                    (error) => {
                        console.error("Error obtaining geolocation:", error);
                        setGeoPermissionStatus('denied');
                    },
                    { enableHighAccuracy: true }
                );
            } else {
                setGeoPermissionStatus('denied');
            }
        };

        init();

        return () => {
            if (heartbeatInterval) {
                clearInterval(heartbeatInterval);
            }
            if (watchId !== null && navigator.geolocation) {
                navigator.geolocation.clearWatch(watchId);
            }
            if (frame) {
                frame.leave().then(() => frame?.destroy());
            }
        };
    }, [roomUrl]);

    if (!roomUrl) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 p-4">
                <div className="bg-white p-8 rounded-lg shadow-md max-w-md text-center">
                    <FiVideo className="text-4xl text-red-500 mx-auto mb-4" />
                    <h2 className="text-xl font-bold mb-2">Enlace no válido</h2>
                    <p className="text-gray-600">Por favor, revisa el enlace proporcionado por tu perito e inténtalo de nuevo.</p>
                </div>
            </div>
        );
    }

    if (geoPermissionStatus === 'requesting') {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 p-4">
                <div className="bg-white p-8 rounded-lg shadow-md max-w-md text-center">
                    <FiMapPin className="text-4xl text-blue-500 mx-auto mb-4 animate-bounce" />
                    <h2 className="text-xl font-bold mb-2">Permiso de Ubicación Requerido</h2>
                    <p className="text-gray-600 mb-4">
                        Para proceder con la videoperitación, necesitamos conocer tu ubicación.
                        Por favor, <strong>permite el acceso a tu ubicación</strong> en el mensaje de tu navegador.
                    </p>
                    <div className="pulse-loader mx-auto"></div>
                </div>
            </div>
        );
    }

    if (geoPermissionStatus === 'denied') {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 p-4">
                <div className="bg-white p-8 rounded-lg shadow-md max-w-md text-center border-t-4 border-red-500">
                    <FiMapPin className="text-4xl text-red-500 mx-auto mb-4" />
                    <h2 className="text-xl font-bold mb-2 text-red-700">Acceso a Ubicación Denegado</h2>
                    <p className="text-gray-600 mb-4">
                        No podemos iniciar la videoperitación sin verificar tu ubicación.
                    </p>
                    <p className="text-sm text-gray-500 text-left bg-gray-50 p-3 rounded border border-gray-200">
                        <strong>¿Cómo solucionarlo?</strong><br />
                        Ve a la configuración de tu navegador (el ícono de candado en la barra de direcciones) y permite el acceso a la ubicación. Luego recarga esta página.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col min-h-screen bg-gray-900">
            <div className="bg-gray-800 text-white p-3 flex justify-between items-center shadow-md">
                <div className="flex items-center gap-2">
                    <FiVideo className="text-blue-400" />
                    <span className="font-medium">Videoperitación</span>
                </div>
                {joined ? (
                    <div className="flex items-center gap-1 text-xs text-green-400 bg-green-900/30 px-2 py-1 rounded">
                        <span className="relative flex h-2 w-2">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                        </span>
                        Conectado
                    </div>
                ) : (
                    <div className="text-xs text-gray-400">Conectando...</div>
                )}
            </div>
            <div className="flex-1 w-full" ref={containerRef}>
                {/* Daily iframe will be injected here */}
            </div>
        </div>
    );
};

export default SalaCliente;
